/**
 * THMV - Calculadora de frete por CEP
 *
 * Substitui o formulário nativo do tema (País + Estado + CEP) por um campo
 * único de CEP, padrão em e-commerce brasileiro.
 *
 * Como o estado (UF) ainda é necessário para a Shopify calcular as faixas de
 * frete corretamente, ele é resolvido automaticamente a partir do CEP usando
 * a API pública ViaCEP. Se a consulta falhar (offline, API fora do ar, CEP
 * não encontrado), o cálculo segue apenas com país + CEP — a Shopify ainda
 * consegue retornar tarifas na maioria dos casos, só com menos precisão.
 *
 * ATENÇÃO: a API de frete da Shopify (/cart/*_shipping_rates.json) calcula
 * sempre com base no CARRINHO ATUAL. Na página de produto, portanto, o
 * resultado reflete o que já está no carrinho — não o produto sendo visto
 * isoladamente. Ver CUSTOMIZACOES.md para detalhes e alternativas.
 */
if (!customElements.get("thmv-shipping-calculator")) {
  customElements.define(
    "thmv-shipping-calculator",
    class ThmvShippingCalculator extends HTMLElement {
      static MAX_ATTEMPTS = 8;
      static POLL_INTERVAL = 2000;
      static STORAGE_KEY = "thmv_shipping_zip";

      constructor() {
        super();
        this.attemptCount = 0;
      }

      connectedCallback() {
        this.form = this.querySelector(".thmv-shipping__form");
        this.input = this.querySelector(".thmv-shipping__input");
        this.submitButton = this.querySelector(".thmv-shipping__submit");
        this.resultsElement = this.querySelector(".thmv-shipping__results");
        this.templateElement = this.querySelector(".thmv-shipping__template");
        this.locationElement = this.querySelector(".thmv-shipping__location");
        this.errorElement = this.querySelector(".thmv-shipping__error");
        this.freeHintElement = this.querySelector(".thmv-shipping__free-hint");

        if (!this.form || !this.input) return;

        this.country = this.dataset.country || "";

        this.form.addEventListener("submit", this.onSubmit.bind(this));
        this.input.addEventListener("input", this.onInput.bind(this));

        this.prefillZip();
      }

      /**
       * Pré-preenche o CEP, na ordem:
       *   1. último CEP usado pelo próprio visitante (localStorage)
       *   2. CEP do endereço padrão, se o cliente estiver logado
       *
       * Não pedimos permissão de geolocalização do navegador: além do prompt
       * ser invasivo logo na entrada, a coordenada do GPS/IP não devolve CEP
       * diretamente (exigiria um serviço de geocodificação reversa) e costuma
       * errar o bairro em áreas urbanas densas.
       */
      prefillZip() {
        let zip = "";
        try {
          zip = window.localStorage.getItem(ThmvShippingCalculator.STORAGE_KEY) || "";
        } catch (error) {
          zip = "";
        }
        if (!zip) zip = this.dataset.defaultZip || "";
        if (!zip) return;

        const digits = zip.replace(/\D/g, "").slice(0, 8);
        if (digits.length !== 8) return;

        this.input.value = `${digits.slice(0, 5)}-${digits.slice(5)}`;
      }

      rememberZip(zip) {
        try {
          window.localStorage.setItem(ThmvShippingCalculator.STORAGE_KEY, zip);
        } catch (error) {
          /* localStorage indisponível (modo privado) — segue sem lembrar */
        }
      }

      /** Máscara de CEP: 00000-000 */
      onInput() {
        const digits = this.input.value.replace(/\D/g, "").slice(0, 8);
        this.input.value =
          digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
      }

      onSubmit(event) {
        event.preventDefault();

        const zip = this.input.value.replace(/\D/g, "");
        if (zip.length !== 8) {
          this.showError("Informe um CEP válido, com 8 dígitos.");
          this.input.focus();
          return;
        }

        this.reset();
        this.startLoading();

        this.loadCart()
          .then((cart) => {
            this.cart = cart;
            if (cart && cart.item_count === 0) {
              this.endLoading();
              this.showError(
                "Adicione o produto ao carrinho para calcular o frete. A Shopify calcula o frete com base nos itens do carrinho.",
              );
              return null;
            }
            return this.lookupAddress(zip).then((address) => {
              if (address && address.city && address.uf) {
                this.showLocation(`${address.city} - ${address.uf}`);
              }
              return this.tryCountries(
                this.getCountryCandidates(),
                this.input.value,
                address ? address.province : "",
              );
            });
          })
          .then((rates) => {
            if (rates === null) return;
            if (rates && rates.length > 0) {
              this.rememberZip(this.input.value);
              this.showResults(rates);
            } else {
              this.showError(
                "Nenhuma opção de entrega encontrada para este CEP.",
              );
            }
          })
          .catch((error) => {
            console.error("[THMV frete] falha ao calcular:", error);
            this.showError(
              "Não foi possível calcular o frete agora. Tente novamente em instantes.",
            );
          });
      }

      /**
       * A API de frete da Shopify calcula sempre sobre o CARRINHO atual — com
       * o carrinho vazio ela falha. Verificamos antes para dar uma mensagem
       * clara em vez de um erro genérico.
       */
      loadCart() {
        return fetch(`${window["routes"]["cart_url"]}.js`, {
          ...webvista.fetchConfig("json", "GET"),
        })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null);
      }

      /**
       * O endpoint da Shopify é sensível ao formato do país. Dependendo da
       * configuração da loja, o valor aceito pode ser o nome em inglês
       * ("Brazil"), o nome localizado ("Brasil") ou o código ISO ("BR").
       * Testamos os candidatos em sequência e usamos o primeiro que retornar
       * tarifas — assim a calculadora não depende de adivinhar o formato certo.
       */
      getCountryCandidates() {
        return [
          this.dataset.country,
          this.dataset.countryAlt,
          this.dataset.countryCode,
        ]
          .map((value) => (value || "").trim())
          .filter(Boolean)
          .filter((value, index, list) => list.indexOf(value) === index);
      }

      tryCountries(countries, zip, province) {
        if (!countries.length) {
          return Promise.reject(
            new Error("Nenhum país configurado para o cálculo de frete."),
          );
        }

        const attempt = (index, lastError) => {
          if (index >= countries.length) {
            if (lastError) throw lastError;
            return [];
          }

          this.attemptCount = 0;
          return this.prepareRates({
            zip: zip,
            country: countries[index],
            province: province,
          })
            .then((rates) => {
              if (rates && rates.length > 0) return rates;
              return attempt(index + 1, lastError);
            })
            .catch((error) => {
              console.warn(
                `[THMV frete] país "${countries[index]}" não aceito:`,
                error.message,
              );
              return attempt(index + 1, error);
            });
        };

        return Promise.resolve().then(() => attempt(0, null));
      }

      /**
       * Resolve cidade/UF a partir do CEP via ViaCEP.
       * Nunca rejeita: falha vira `null` para o cálculo seguir sem a UF.
       */
      lookupAddress(zip) {
        return fetch(`https://viacep.com.br/ws/${zip}/json/`)
          .then((response) => (response.ok ? response.json() : null))
          .then((data) => {
            if (!data || data.erro || !data.uf) return null;
            return {
              city: data.localidade || "",
              uf: data.uf,
              province: ThmvShippingCalculator.UF_NAMES[data.uf] || "",
            };
          })
          .catch(() => null);
      }

      /** Dispara o cálculo e devolve as tarifas encontradas (array). */
      prepareRates(address) {
        const shipping_address = {
          country: address.country,
          province: address.province,
          zip: address.zip,
        };

        return fetch(
          `${window["routes"]["cart_url"]}/prepare_shipping_rates.json`,
          {
            ...webvista.fetchConfig(),
            ...{ body: JSON.stringify({ shipping_address }) },
          },
        )
          .then((response) =>
            response.text().then((text) => ({ response, text })),
          )
          .then(({ response, text }) => {
            if (!response.ok) {
              throw new Error(
                `prepare_shipping_rates ${response.status}: ${text.slice(0, 200)}`,
              );
            }

            let parsed = null;
            if (text && text.trim()) {
              try {
                parsed = JSON.parse(text);
              } catch (error) {
                parsed = null;
              }
            }
            if (parsed !== null && parsed.ok === false) {
              throw new Error(
                `prepare_shipping_rates retornou ok=false: ${text.slice(0, 200)}`,
              );
            }
            return this.asyncRates({ shipping_address });
          });
      }

      /**
       * A Shopify calcula as tarifas de forma assíncrona (o app transportador
       * — Frenet, no caso desta loja — é consultado em background), então é
       * preciso pesquisar o resultado algumas vezes até ele ficar pronto.
       */
      asyncRates(params) {
        return fetch(
          `${window["routes"]["cart_url"]}/async_shipping_rates.json?` +
            this.serialize(params),
          { ...webvista.fetchConfig("json", "GET") },
        )
          .then((response) => {
            if (!response.ok) {
              throw new Error(`async_shipping_rates ${response.status}`);
            }
            return response.json();
          })
          .then((response) => {
            const rates = response && response["shipping_rates"];
            if (rates && rates.length > 0) return rates;

            if (++this.attemptCount >= ThmvShippingCalculator.MAX_ATTEMPTS) {
              return [];
            }

            return new Promise((resolve) => {
              setTimeout(
                () => resolve(this.asyncRates(params)),
                ThmvShippingCalculator.POLL_INTERVAL,
              );
            });
          });
      }

      showResults(rates) {
        this.endLoading();
        if (!this.resultsElement || !this.templateElement) return;

        const template = this.templateElement.content;
        const fragment = document.createDocumentFragment();

        rates.forEach((rate) => {
          const clone = document.importNode(template, true);

          const nameElement = clone.querySelector(".thmv-shipping__result-name");
          if (nameElement) {
            nameElement.textContent =
              rate["presentment-name"] || rate["name"] || "";
          }

          const priceElement = clone.querySelector(
            ".thmv-shipping__result-price",
          );
          if (priceElement) {
            priceElement.textContent = this.formatPrice(rate["price"]);
          }

          const dateElement = clone.querySelector(".thmv-shipping__result-date");
          if (dateElement) {
            const label = this.formatDeliveryDate(rate);
            if (label) {
              dateElement.textContent = label;
            } else {
              dateElement.remove();
            }
          }

          fragment.appendChild(clone);
        });

        this.resultsElement.innerHTML = "";
        this.resultsElement.appendChild(fragment);
        this.resultsElement.hidden = false;

        this.renderFreeShippingHint(rates);
      }

      /**
       * Monta o texto de prazo já no formato brasileiro (DD/MM/AAAA).
       * A Shopify devolve datas em ISO (AAAA-MM-DD).
       */
      formatDeliveryDate(rate) {
        const range = rate["delivery_range"];
        let start = rate["delivery_date"];
        let end = null;

        if (Array.isArray(range) && range.length > 0) {
          start = range[0];
          if (range.length > 1 && range[1] !== range[0]) end = range[1];
        }

        const startLabel = this.toBrDate(start);
        if (!startLabel) return "";

        const endLabel = this.toBrDate(end);
        return endLabel
          ? `Entrega entre ${startLabel} e ${endLabel}`
          : `Entrega até ${startLabel}`;
      }

      toBrDate(value) {
        if (!value || typeof value !== "string") return "";
        const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!match) return "";
        return `${match[3]}/${match[2]}/${match[1]}`;
      }

      /** Frete zerado aparece como "Grátis" (mais claro que "R$ 0,00"). */
      formatPrice(price) {
        if (parseFloat(price) === 0) return "Grátis";
        const template = window["priceFormatTemplate"];
        if (!template) return price;
        return template.replace(/0([,.]0{0,2})?/, price);
      }

      /**
       * Frete grátis. Reusa o mesmo limite da barra de progresso do tema
       * (settings.free_shipping_threshold) para não haver dois valores
       * divergentes na loja.
       *
       * Existem duas formas de configurar frete grátis na Shopify, e elas se
       * comportam de forma diferente aqui:
       *
       * 1. Como TARIFA de envio (Configurações → Envio → faixa "Frete grátis"
       *    com condição de preço). Nesse caso a própria API devolve uma tarifa
       *    de valor zero, e ela aparece na lista como "Grátis".
       *
       * 2. Como DESCONTO automático (Descontos → frete grátis acima de X).
       *    Descontos NÃO entram na API de tarifas — ela devolve o preço cheio
       *    do transportador (Frenet, aqui), e o abatimento só acontece no
       *    checkout. Por isso, quando o carrinho já atingiu o limite mas
       *    nenhuma tarifa zerada voltou, avisamos explicitamente que o frete
       *    grátis será aplicado no checkout — em vez de deixar o cliente achar
       *    que vai pagar o valor cheio.
       */
      renderFreeShippingHint(rates) {
        if (!this.freeHintElement) return;

        const hasFreeRate = (rates || []).some(
          (rate) => parseFloat(rate["price"]) === 0,
        );
        if (hasFreeRate) {
          this.showFreeHint("Você já tem frete grátis 🎉");
          return;
        }

        const threshold = parseFloat(this.dataset.freeThreshold);
        if (!threshold || !this.cart) return;

        const rate =
          (window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
        const thresholdCents = Math.round(threshold * rate) * 100;
        const missing = thresholdCents - this.cart.total_price;

        if (missing <= 0) {
          this.showFreeHint(
            "Seu pedido já tem frete grátis — o desconto é aplicado no checkout.",
          );
          return;
        }

        this.showFreeHint(
          `Faltam ${this.formatPrice(
            (missing / 100).toFixed(2),
          )} para frete grátis.`,
        );
      }

      showFreeHint(text) {
        if (!this.freeHintElement) return;
        this.freeHintElement.textContent = text;
        this.freeHintElement.hidden = false;
      }

      showLocation(text) {
        if (!this.locationElement) return;
        this.locationElement.textContent = text;
        this.locationElement.hidden = false;
      }

      showError(message) {
        this.endLoading();
        if (!this.errorElement) return;
        this.errorElement.textContent = message;
        this.errorElement.hidden = false;
      }

      reset() {
        if (this.resultsElement) {
          this.resultsElement.innerHTML = "";
          this.resultsElement.hidden = true;
        }
        if (this.locationElement) this.locationElement.hidden = true;
        if (this.errorElement) this.errorElement.hidden = true;
        if (this.freeHintElement) this.freeHintElement.hidden = true;
      }

      startLoading() {
        if (!this.submitButton) return;
        this.submitButton.classList.add("loading");
        this.submitButton.setAttribute("disabled", "disabled");
      }

      endLoading() {
        if (!this.submitButton) return;
        this.submitButton.classList.remove("loading");
        this.submitButton.removeAttribute("disabled");
      }

      serialize(obj, prefix) {
        return Object.keys(obj)
          .filter((key) => Object.prototype.hasOwnProperty.call(obj, key))
          .map((key) => {
            const k = prefix ? `${prefix}[${key}]` : key;
            const v = obj[key];
            return v !== null && typeof v === "object"
              ? this.serialize(v, k)
              : `${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
          })
          .join("&");
      }

      /**
       * A Shopify espera o nome do estado por extenso (não a sigla) no campo
       * `province`. Mapeamento fixo das 27 unidades federativas.
       */
      static UF_NAMES = {
        AC: "Acre",
        AL: "Alagoas",
        AP: "Amapá",
        AM: "Amazonas",
        BA: "Bahia",
        CE: "Ceará",
        DF: "Distrito Federal",
        ES: "Espírito Santo",
        GO: "Goiás",
        MA: "Maranhão",
        MT: "Mato Grosso",
        MS: "Mato Grosso do Sul",
        MG: "Minas Gerais",
        PA: "Pará",
        PB: "Paraíba",
        PR: "Paraná",
        PE: "Pernambuco",
        PI: "Piauí",
        RJ: "Rio de Janeiro",
        RN: "Rio Grande do Norte",
        RS: "Rio Grande do Sul",
        RO: "Rondônia",
        RR: "Roraima",
        SC: "Santa Catarina",
        SP: "São Paulo",
        SE: "Sergipe",
        TO: "Tocantins",
      };
    },
  );
}
